import React from "react";

/**
 * Standardized error state with status-specific copy.
 *
 * Usage:
 *   <ErrorState status={403} retry={() => refetch()} />
 *   <ErrorState status={500} title="Custom override" retry={...} />
 *
 * Status-driven defaults (overridable via props):
 *   401 / 403 → amber: "You don't have access here"
 *   404       → amber: "We can't find that"
 *   5xx       → red:   "Our system hiccuped"
 *   network   → amber: "You appear to be offline"
 *
 * The audit found pages rendering empty states where the underlying call
 * actually 403'd — admins thought the data was empty when access was
 * denied. Using this primitive prevents that whole class of confusion.
 */

const PRESETS = {
  401: {
    tone: "amber",
    title: "You're signed out",
    description: "Sign back in to continue.",
  },
  403: {
    tone: "amber",
    title: "Restricted access",
    description: "You don't have permission to see this. Ask your workspace admin to enable access.",
  },
  404: {
    tone: "amber",
    title: "Not found",
    description: "We couldn't find what you're looking for. It may have been moved or deleted.",
  },
  500: {
    tone: "red",
    title: "Something broke on our side",
    description: "This is on us — try again in a moment. If it keeps happening, contact support.",
  },
  502: { tone: "red", title: "We're having trouble reaching the server", description: "Give it a moment and retry." },
  503: { tone: "red", title: "Service is briefly unavailable", description: "We'll be back shortly. Try again." },
  504: { tone: "red", title: "Request timed out", description: "The server took too long. Try again." },
  network: {
    tone: "amber",
    title: "You appear to be offline",
    description: "Reconnect to Wi-Fi or cellular data and we'll resume.",
  },
};

const TONES = {
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    icon: "text-amber-600 bg-amber-100",
    title: "text-amber-900",
    text: "text-amber-800",
  },
  red: {
    bg: "bg-red-50",
    border: "border-red-200",
    icon: "text-red-600 bg-red-100",
    title: "text-red-900",
    text: "text-red-800",
  },
};

function pickPreset(status) {
  if (status === "network") return PRESETS.network;
  if (typeof status !== "number") return PRESETS[500];
  if (PRESETS[status]) return PRESETS[status];
  if (status >= 500) return PRESETS[500];
  if (status === 401 || status === 403) return PRESETS[403];
  return PRESETS[500];
}

export default function ErrorState({
  status = 500,
  title,
  description,
  retry,
  support,
  className = "",
}) {
  const preset = pickPreset(status);
  const tone = TONES[preset.tone];
  const finalTitle = title || preset.title;
  const finalDescription = description || preset.description;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`flex flex-col items-center text-center py-10 px-6 rounded-card border ${tone.bg} ${tone.border} ${className}`}
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${tone.icon}`}
        aria-hidden="true"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {preset.tone === "red" ? (
            <>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </>
          ) : (
            <>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </>
          )}
        </svg>
      </div>
      <div className={`text-sm font-semibold ${tone.title}`}>{finalTitle}</div>
      {finalDescription && (
        <p className={`text-xs mt-1.5 max-w-md leading-relaxed ${tone.text}`}>
          {finalDescription}
        </p>
      )}
      {typeof status === "number" && (
        <div className="text-2xs text-vf-muted mt-3 font-mono">Error {status}</div>
      )}
      {(retry || support) && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          {retry && (
            <button
              type="button"
              onClick={retry}
              className="px-3.5 py-1.5 rounded-md bg-white text-vf-base text-xs font-semibold border border-subtle hover:bg-vf-tint transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
            >
              Try again
            </button>
          )}
          {support && (
            <button
              type="button"
              onClick={support.onClick}
              className="px-3.5 py-1.5 rounded-md text-vf-primary text-xs font-semibold underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
            >
              {support.label || "Contact support"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
